import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const headCellSx = {
  fontWeight: 700,
  color: ui.primaryDark,
  backgroundColor: ui.primarySoft,
  fontSize: "0.8rem",
  py: 1.5,
  borderBottom: `1px solid ${ui.border}`,
};

const bodyCellSx = {
  color: ui.text,
  py: 1.75,
  fontSize: "0.875rem",
  borderBottom: `1px solid ${ui.border}`,
};

const mobileCardSx = {
  border: `1px solid ${ui.border}`,
  borderRadius: "12px",
  p: 2,
  backgroundColor: "#fff",
};

const outlineButtonSx = (color, soft) => ({
  flex: 1,
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "8px",
  color,
  borderColor: color,
  "&:hover": { borderColor: color, backgroundColor: soft },
  "&.Mui-disabled": { color: "#b0b7be", borderColor: "#e0e4e8" },
});

const PillChip = ({ label, tone = "neutral" }) => {
  const tones = {
    danger: { bg: ui.dangerSoft, color: ui.danger },
    success: { bg: ui.successSoft, color: ui.success },
    neutral: { bg: "#eef1f4", color: "#4b5563" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        borderRadius: "8px",
        backgroundColor: t.bg,
        color: t.color,
      }}
    />
  );
};

const EmptyState = ({ icon: Icon = InboxOutlinedIcon, title, message }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ui.text }}>
      {title}
    </Typography>
    {message && (
      <Typography
        variant="body2"
        sx={{ color: ui.muted, mt: 0.5, maxWidth: 360, mx: "auto" }}
      >
        {message}
      </Typography>
    )}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box
    role="status"
    aria-label="Loading"
    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
  >
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: ui.radius,
      border: `1px solid ${ui.border}`,
      backgroundColor: "#fff",
      boxShadow: ui.shadow,
      overflow: "visible",
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: ui.text,
            pl: 1.5,
            borderLeft: `4px solid ${ui.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const UsersList = ({
  usersLoading,
  users,
  pagedUsers,
  rowsPerPage,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  handleOpenModal,
}) => {
  const renderActions = (user, withLabels) => {
    const isDeleted = user.deleted;

    if (withLabels) {
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => handleOpenModal(user, "Details")}
            disabled={isDeleted}
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={outlineButtonSx(ui.success, ui.successSoft)}
          >
            Details
          </Button>
          <Button
            onClick={() => handleOpenModal(user, "Edit")}
            disabled={isDeleted}
            startIcon={<EditOutlinedIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={outlineButtonSx(ui.primary, ui.primarySoft)}
          >
            Edit
          </Button>
          <Button
            onClick={() => handleOpenModal(user, "Delete")}
            disabled={isDeleted}
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={outlineButtonSx(ui.danger, ui.dangerSoft)}
          >
            Delete
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Details">
          <span>
            <IconButton
              onClick={() => handleOpenModal(user, "Details")}
              disabled={isDeleted}
              size="small"
              aria-label={`View details for ${user.fullName}`}
              sx={{
                color: ui.success,
                "&:hover": { backgroundColor: ui.successSoft },
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit">
          <span>
            <IconButton
              onClick={() => handleOpenModal(user, "Edit")}
              disabled={isDeleted}
              size="small"
              aria-label={`Edit ${user.fullName}`}
              sx={{
                color: ui.primary,
                "&:hover": { backgroundColor: ui.primarySoft },
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton
              onClick={() => handleOpenModal(user, "Delete")}
              disabled={isDeleted}
              size="small"
              aria-label={`Delete ${user.fullName}`}
              sx={{
                color: ui.danger,
                "&:hover": { backgroundColor: ui.dangerSoft },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  return (
    <SectionCard title="Users">
      {usersLoading ? (
        <ListSkeleton rows={rowsPerPage > 5 ? 6 : rowsPerPage} />
      ) : users.length === 0 ? (
        <EmptyState
          icon={PeopleAltOutlinedIcon}
          title="No users found"
          message="Users you create will show up here."
        />
      ) : (
        <>
          {/* Desktop / tablet table */}
          <Box
            sx={{
              display: { xs: "none", sm: "block" },
              overflowX: "auto",
              border: `1px solid ${ui.border}`,
              borderRadius: "12px",
            }}
          >
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  {["Employee ID", "Name", "Job title", "Actions"].map((h) => (
                    <TableCell key={h} sx={headCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    hover={!user.deleted}
                    sx={{
                      backgroundColor: user.deleted ? "#f3f4f6" : "inherit",
                      "& td": { opacity: user.deleted ? 0.65 : 1 },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, wordBreak: "break-word" }}>
                      {user.employeeId || "N/A"}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, wordBreak: "break-word" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                        >
                          {user.fullName}
                        </Typography>
                        {user.deleted && (
                          <PillChip label="Deleted" tone="danger" />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: ui.muted }}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, wordBreak: "break-word" }}>
                      {user.jobTitle}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {renderActions(user, false)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Mobile cards */}
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {pagedUsers.map((user) => (
              <Box
                key={user.id}
                sx={{
                  ...mobileCardSx,
                  backgroundColor: user.deleted ? "#f3f4f6" : "#fff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1.5,
                    opacity: user.deleted ? 0.65 : 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: ui.text,
                        wordBreak: "break-word",
                      }}
                    >
                      {user.fullName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: ui.muted, wordBreak: "break-word" }}
                    >
                      {user.email}
                    </Typography>
                  </Box>
                  {user.deleted && <PillChip label="Deleted" tone="danger" />}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 1,
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: "10px",
                    backgroundColor: ui.surface,
                    opacity: user.deleted ? 0.65 : 1,
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: ui.muted }}>
                      Employee ID
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {user.employeeId || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: ui.muted }}>
                      Job title
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {user.jobTitle}
                    </Typography>
                  </Box>
                </Box>
                {renderActions(user, true)}
              </Box>
            ))}
          </Box>
        </>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          mt: 2,
          color: ui.text,
          "& .MuiTablePagination-toolbar": {
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "flex-end" },
            px: 0,
          },
          "& .MuiTablePagination-spacer": {
            display: { xs: "none", sm: "block" },
          },
        }}
      />
    </SectionCard>
  );
};

export default UsersList;